'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import InlineSVG from '@src/components/Shared/InlineSVG';
import Expander from '@src/components/Shared/Expander';
import ProductBox from '@src/components/Shared/ProductBox';

import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import HeroValdProd from '@src/components/Sections/HeroValdProd';

import Profile from '@src/components/Icons/Profile';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { DynamoPageClientPropTypes } from './DynamoPage.types';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';
import Testimonials from '@src/components/Sections/Testimonials';

const DynamoPageClient: React.FC<DynamoPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/dynamo`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[3.2rem]! mb-[0rem]!'
        imgClassName='mt-[2rem] md:absolute md:right-[0] md:bottom-[-10rem] md:w-[58rem] lg:bottom-[0] lg:relative lg:w-[60rem] xl:left-[-15rem] xl:w-[70rem] xl:left-[0rem] 2xl:w-[70rem] 2xl:left-[-20rem] 3xl:left-[-20rem] 2xl:bottom-[0] xsm:min-h-[34.8297rem] md:min-h-[58rem] lg:min-h-[60rem] xl:min-h-[70rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] xsm:min-h-[7.9828rem] md:min-h-[7.4234rem] lg:min-h-[10.3797rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%] lg:mt-[-20rem]'
      />

      <ImageTextSecondary
        id={page?.hundredsOfTests?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.hundredsOfTests?.items?.[0]?.title}
        imgURL={page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.url}
        priority={true}
        imgFirstRatio={
          page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.width /
          page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.height
        }
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.hundredsOfTests?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.hundredsOfTests?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.modularity?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#232323] '
        className='3xl:py-[10rem]! overflow-visible! py-[5rem]'
        pretitle={page?.modularity?.items?.[0]?.preTitle}
        title={page?.modularity?.items?.[0]?.title}
        titleClassName='text-[#fff]'
        imgURL={page?.modularity?.items?.[0]?.mediaPrimary?.url}
        imgSecondClassName='lg:[&>img]:pl-[3.2rem] lg:[&>img]:left-[5rem]! 2xl:[&>img]:left-[0rem]! lg:w-[70%]'
        textContainerClassName='lg:pr-[2.4rem]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-[#fff] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.modularity?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        id={page?.engagement?.items?.[0]?.scrollTo?.slice(1)}
        className='2xl:mb-[0]!'
        pretitle={page?.engagement?.items?.[0]?.preTitle}
        title={page?.engagement?.items?.[0]?.title}
        imgURL={page?.engagement?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='pb-[0.5rem] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.engagement?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.useability?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible! py-[0]! lg:my-[0rem]!'
        pretitle={page?.useability?.items?.[0]?.preTitle}
        title={page?.useability?.items?.[0]?.title}
        imgURL={page?.useability?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.useability?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container
        id={page?.efficiency?.items?.[0]?.scrollTo?.slice(1)}
        className='mb-[0rem]! overflow-visible! px-[0rem]! md:flex md:flex-col lg:flex-col'
        background='bg-background-primary'
      >
        <NextImage
          src={page?.efficiency?.items?.[0]?.mediaPrimary?.url}
          alt='A portrait'
          className='min-h-[80rem] overflow-visible! sm:min-h-[75rem] md:min-h-[90rem]'
          imgClassName='object-[80%_20%]! absolute! md:object-[95%_20%]! lg:object-center!'
        >
          <div className='flex h-full flex-col md:flex-row lg:flex-col'>
            <SectionHeader
              className='mt-auto justify-center px-[2.4rem] pt-[6rem] text-[#fff] md:mr-auto md:mb-[3.4rem] md:text-left lg:ml-auto lg:items-center lg:justify-center lg:pb-[unset] lg:text-center'
              pretitle={page?.efficiency?.items?.[0]?.preTitle}
              title={page?.efficiency?.items?.[0]?.title}
              titleClassName='max-w-[71.6rem] text-[#fff] text-[4.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[5.4rem] md:max-w-[unset] md:leading-[4.6rem] font-light'
            >
              <RenderBodyCopy
                className='max-w-[57rem]'
                bodyCopy={page?.efficiency?.items?.[0]?.bodyCopy?.json?.content}
              />
            </SectionHeader>

            <div className='relative z-5 mt-[3.2rem] flex flex-col bg-[#2F2F2F] py-[3.2rem] md:mt-[0] md:ml-auto lg:mt-[2.4rem] lg:mb-auto lg:ml-[unset] lg:flex-row lg:gap-[3.2rem] lg:bg-transparent'>
              <InlineSVG
                className='mx-auto max-w-[55rem] px-[2.4rem] md:mt-[0rem] md:hidden md:px-[6rem]'
                src={page?.efficiency?.items?.[0]?.mediaQuaternary?.url}
                renderAt={['mobile']}
              />

              <InlineSVG
                className='mb-[2.4rem] hidden md:mx-auto md:block md:w-[15rem] md:px-[1rem] lg:order-1 lg:mb-[-1rem] lg:hidden'
                src={page?.efficiency?.items?.[0]?.mediaTertiary?.url}
                renderAt={['tablet']}
              />

              <InlineSVG
                className='relative z-1 hidden lg:block lg:w-[110rem]'
                src={page?.efficiency?.items?.[0]?.mediaSecondary?.url}
                renderAt={['desktop']}
              />

              <div className='relative z-1 -order-1 flex items-center justify-center gap-[1.6rem] md:mt-auto md:mb-[3.2rem] md:px-[1rem] lg:mt-[unset] lg:mb-[unset] lg:hidden lg:pl-[2.4rem]'>
                <Profile className='h-[4rem] w-[4rem] lg:h-[5.4rem] lg:w-[5.4rem]' />
                <p className='text-[1.6rem] leading-[2rem] font-normal whitespace-nowrap text-[#fff] lg:text-[2rem]'>
                  <span className='font-medium'>Shoulder I-Y-T</span> <br /> Test Battery
                </p>
              </div>
              <Expander className='absolute right-[-2] z-0 hidden self-center bg-[#2F2F2F] lg:bottom-[33%] lg:left-[0] lg:block lg:h-[33.8%] lg:w-[45%]' />
            </div>
          </div>
        </NextImage>
      </Container>

      <ImageTextSecondary
        id={page?.visibility?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible! pt-[6rem] pb-[0]!'
        pretitle={page?.visibility?.items?.[0]?.preTitle}
        title={page?.visibility?.items?.[0]?.title}
        imgURL={page?.visibility?.items?.[0]?.mediaPrimary?.url}
        imgSecondClassName='2xl:relative 2xl:bottom-[-1.3735rem]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.visibility?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.visibility?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container id={page?.versatility?.items?.[0]?.scrollTo?.slice(1)} className='mb-[0]!'>
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.versatility?.items?.[0]?.preTitle}
          title={page?.versatility?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] xl:text-[4.8rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        />

        <div className='pt-[4.2rem] pb-[1rem] md:py-[8rem] lg:mt-[-10rem]'>
          <ProductBox
            className='md:locale-es:max-w-[60rem]! mb-[5.2rem] text-left! sm:text-center md:max-w-[46rem]! lg:relative lg:bottom-[-10rem] md:[&>div>p]:max-w-[46rem]'
            buttonClassName='justify-start! sm:gap-[1rem] [&>div>[data-pseudo]]:locale-fr:before:px-[2rem]'
            product={page?.dynamoPlus?.items?.[0]}
          />

          <InlineSVG
            className='mb-[2.4rem] w-full md:hidden lg:order-1 lg:mb-[unset]'
            src={page?.dynamoPlus?.items?.[0]?.mediaTertiary?.url}
            renderAt={['mobile']}
          />

          <InlineSVG
            className='mb-[2.4rem] hidden md:order-1 md:mb-[-1rem] md:block md:w-full lg:hidden'
            src={page?.dynamoPlus?.items?.[0]?.mediaSecondary?.url}
            renderAt={['tablet']}
          />

          <InlineSVG
            className='hidden lg:order-1 lg:block'
            src={page?.dynamoPlus?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop']}
          />
        </div>
      </Container>

      <Container
        id={page?.dynamoLight?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='mb-[0rem]!'
      >
        <div className='pt-[3.2rem] pb-[0.5rem] md:py-[8rem]'>
          <ProductBox
            className='md:bg-background-secondary! md:locale-es:max-w-[60rem]! mb-[5.2rem] text-left! sm:text-center md:max-w-[46rem]! lg:relative lg:bottom-[-10rem] lg:ml-auto md:[&>div>p]:max-w-[46rem]'
            buttonClassName='justify-start! sm:gap-[1rem] [&>div>[data-pseudo]]:locale-fr:before:px-[2rem]'
            product={page?.dynamoLight?.items?.[0]}
          />

          <InlineSVG
            className='mb-[2.4rem] w-full lg:order-1 lg:mb-[unset] lg:hidden'
            src={page?.dynamoLight?.items?.[0]?.mediaSecondary?.url}
            renderAt={['mobile', 'tablet']}
          />

          <InlineSVG
            className='mb-[2.4rem] hidden md:order-1 md:mb-[-1rem] md:w-full lg:block'
            src={page?.dynamoLight?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop']}
          />
        </div>
      </Container>

      <Container
        id={page?.dynamoMax?.items?.[0]?.scrollTo?.slice(1)}
        className='xsm:before:h-[440px] 3xl:px-[4rem]! px-[0]! pb-[2.4rem]! before:absolute before:block before:h-[466px] before:w-full before:bg-[#25282A] before:content-[""] sm:before:h-[285px] md:before:h-[280px] lg:px-[6.4rem]! lg:pt-[7.2rem] lg:before:hidden xl:px-[12.8rem]!'
        background='bg-[#181818]'
      >
        <ProductBox
          className='md:locale-es:max-w-[60rem]! lg:locale-fr:max-w-[53rem]! pt-[2.4rem] pb-[4rem] text-left! md:rounded-none! md:border-0 md:bg-[unset]! md:px-[0] lg:relative lg:z-15 lg:mt-[-1.6rem] lg:max-w-[46rem]! lg:rounded-[2.4rem]! lg:bg-[#25282A]! lg:text-center 2xl:static! 2xl:z-[unset] md:[&>div>p]:max-w-[46rem] [&>p]:text-[#C6C6C6]'
          buttonClassName='[&>div>a]:relative [&>div>a]:z-20 justify-start! px-[2.4rem] sm:gap-[1rem] [&>div>a[data-pseudo]]:text-primary! [&>div>a[data-pseudo]]:before:hidden'
          headingClassName='text-[4rem] text-[#fff] mb-[0] px-[2.4rem]'
          bodyCopyClassName='text-[#C3C3C3] text-[2rem] leading-[3rem] px-[2.4rem]'
          product={page?.dynamoMax?.items?.[0]}
          announcement={page?.dynamoMax?.items?.[0]?.preTitle}
        />

        <InlineSVG
          className='xl:max-auto 3xl:mb-[-11.5rem] 3xl:ml-[0] 3xl:max-w-[unset] relative z-10 hidden w-full lg:mt-[-25rem] lg:mb-[-8rem] lg:block xl:mb-[-9rem] xl:ml-[10rem] xl:max-w-[117rem]'
          src={page?.dynamoMax?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop']}
        />
        <InlineSVG
          className='relative z-[unset] hidden pb-[2rem] md:mt-[-11rem] md:mb-[-4rem] md:block md:w-full lg:hidden'
          src={page?.dynamoMax?.items?.[0]?.mediaSecondary?.url}
          renderAt={['tablet']}
        />
        <InlineSVG
          className='relative z-[unset] mt-[-4rem] mb-[-2.3rem] md:mb-[-1rem] md:hidden md:w-full'
          src={page?.dynamoMax?.items?.[0]?.mediaTertiary?.url}
          renderAt={['mobile']}
        />
      </Container>

      <Container id={page?.dynamoMaxVideo?.items?.[0]?.scrollTo?.slice(1)} className='my-[4rem]!'>
        <SectionHeader
          className='mb-[-1rem] justify-center lg:pb-[unset]'
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.dynamoMaxVideo?.items?.[0]?.preTitle}
          title={page?.dynamoMaxVideo?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[45rem] inline-block'
          align='center'
        >
          <>
            <SocialEmbeds
              className='overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
              data={page?.dynamoMaxVideo?.items?.[0]?.extraAssetsCollection?.items?.[0]}
            />
          </>
        </SectionHeader>
      </Container>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        bgWhite={true}
      />

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mb-[0]! pt-[4rem] pb-[0]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          className='[&>div>div>ul]:gap-[0] [&>div>div>ul>li:first-child:has(>div>span)>div>div]:max-w-sm md:[&>div>div>ul>li:first-child:has(>div>span)>div>div]:max-w-[27rem]'
          headerImgL='w-[11rem] lg:w-[13rem] lg:mt-[-4rem]'
          headerImgR='w-[8rem] lg:w-[10rem] lg:mt-[-4rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts
        data={page?.valdSystems?.items?.[0]}
        background='bg-background-tertiary'
        className='py-[8rem]'
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default DynamoPageClient;
